// src/components/modals/AssignCourseProgramModal.jsx
//
// Lets a supplier choose whether a course is public
// (SUPPLIER_INDEPENDENT) or belongs to one of their own GEFEN programs
// (PROGRAM). Mirrors ChangeProgramStatusModal's shape.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../contexts/UserContext';
import SpinnerOverlay from '../ui/SpinnerOverlay';

export default function AssignCourseProgramModal({ isOpen, onClose, course, programs, loading, onSubmit }) {
  const { showAlert } = useUser();
  const [selected, setSelected] = useState(null); // null = public
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected(course?.programId ?? null);
  }, [isOpen, course]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !course) return null;

  const currentProgramId = course?.programId ?? null;
  const unchanged = selected === currentProgramId;
  const list = programs || [];

  const handleSave = async () => {
    if (unchanged) return onClose?.();
    setSubmitting(true);
    try {
      await onSubmit?.(selected);
      onClose?.();
    } catch (err) {
      showAlert?.(err?.message || 'עדכון השיוך נכשל', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose} />
      {submitting && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      <div
        className="relative z-[1] w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20 flex flex-col max-h-[85dvh] overflow-hidden"
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
                <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base leading-tight">ציבורי / תוכנית גפ״ן</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{course.title}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-5 sm:px-6 py-4 space-y-2 overflow-y-auto">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className={`w-full flex items-start gap-3 rounded-xl border px-3.5 py-3 text-right transition ${
              selected === null ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <span
              className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                selected === null ? 'border-indigo-500' : 'border-gray-300'
              }`}
            >
              {selected === null && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
            </span>
            <span className="min-w-0">
              <span className={`block text-sm font-semibold ${selected === null ? 'text-indigo-700' : 'text-gray-800'}`}>
                קורס ציבורי
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">
                זמין לכל תלמיד, תשלום רגיל בלבד — ללא שימוש בארנק גפ״ן
              </span>
            </span>
          </button>

          {loading ? (
            <SpinnerOverlay variant="glow" text="טוען תוכניות..." dark={false} blur={false} />
          ) : list.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500">
              אין לכם עדיין תוכנית גפ״ן מאושרת. תוכניות בסטטוס טיוטה או ממתין לאישור אינן זמינות לשיוך.
            </div>
          ) : (
            list.map((p) => {
              const active = p.id === selected;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p.id)}
                  className={`w-full flex items-start gap-3 rounded-xl border px-3.5 py-3 text-right transition ${
                    active ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      active ? 'border-indigo-500' : 'border-gray-300'
                    }`}
                  >
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-800'}`}>
                      {p.name}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      קורס זה ישולם באמצעות ארנק גפ״ן של התוכנית
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm px-5 sm:px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-40"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || unchanged}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold shadow-md shadow-indigo-200 hover:shadow-lg hover:-translate-y-px transition-all duration-150 disabled:opacity-40 disabled:translate-y-0 disabled:shadow-none"
          >
            {submitting ? 'שומר…' : 'שמור שיוך'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}